import * as React from 'react'
import { Query } from 'react-apollo'
import { defineMessages, FormattedMessage, MessageDescriptor } from 'react-intl'
import QRCode from 'qrcode'
import isMobile from 'ismobilejs'

import { getBankidSession } from './gql'
import ApolloErrorMessage from '../../../../components/apollo-error-message'
import {
  DooerComGetBankIdSessionV2,
  DooerComGetBankIdSessionV2Variables,
  DooerComGetBankIdSessionV2_bankIdSessionV2,
} from './__generated__/DooerComGetBankIdSessionV2'
import { BankIdOrderHintCode, BankIdOrderStatus } from '../../../../__generated__/globalTypes'
import { css } from 'glamor'
import { ApolloError } from 'apollo-client'

type Messages = Record<string, MessageDescriptor>

const qrImageFromToken = (autoStartToken: string) => QRCode.toDataURL(`bankid:///?autostarttoken=${autoStartToken}`)
const qrImageFromQRInput = (bankIdQRImage: string) => QRCode.toDataURL(bankIdQRImage, { errorCorrectionLevel: 'H' })

const messages: Messages = defineMessages({
  // pending orders
  outstandingTransaction: {
    id: 'signup.bankid-session-status.status-outstanding-transaction',
    defaultMessage: 'Starta BankID för att påbörja signering.',
  },
  noClient: {
    id: 'signup.bankid-session-status.status-no-client',
    defaultMessage: 'Starta BankID för att påbörja signering.',
  },
  userSign: {
    id: 'signup.bankid-session-status.status-user-sign',
    defaultMessage: 'Legitimera eller skriv under i BankID-appen',
  },

  // failed orders
  expiredTransaction: {
    id: 'signup.bankid-session-status.status-expired-transaction',
    defaultMessage:
      'BankID-appen svarar inte. Kontrollera att appen körs och att du har tillgång till internet. Om du inte har ett BankID kan du hämta ett från din bank. Försök igen.',
  },
  certificateErr: {
    id: 'signup.bankid-session-status.status-certificate-err',
    defaultMessage:
      'BankID:t du försöker använda är utgånget. Använd ett annat BankID eller beställ ett nytt från din internetbank.',
  },
  userCancel: {
    id: 'signup.bankid-session-status.status-user-cancel',
    defaultMessage: 'Åtgärden avbruten.',
  },
  cancelled: {
    id: 'signup.bankid-session-status.status-cancelled',
    defaultMessage: 'Åtgärden avbruten. Vänligen försök igen.',
  },

  // other
  pending: {
    id: 'signup.bankid-session-status.status-pending',
    defaultMessage: 'Var god vänta…',
  },
  complete: {
    id: 'signup.bankid-session-status.status-complete',
    defaultMessage: 'Åtgärden utförd.',
  },
  unknown: {
    id: 'signup.bankid-session-status.status-unknown',
    defaultMessage: 'Okänt fel. Vänligen försök igen.',
  },

  // open on device
  openOnDevice: {
    id: 'signup.bankid-session-status.message-open-bankid-on-device',
    defaultMessage: 'Klicka här för att öppna BankID på den här enheten',
  },

  // Bankid qr code guide steps
  bankIdQrGuideHeading: {
    id: 'signup.bankid-session-status.bankid-qr-guide.heading',
    defaultMessage: 'Legitimera med mobilt BankID',
  },
  step1: {
    id: 'signup.bankid-session-status.steps.step-1',
    defaultMessage: '1. Öppna BankID-appen i din mobil',
  },
  step2: {
    id: 'signup.bankid-session-status.steps.step-2',
    defaultMessage: '2. Klicka på QR-symbolen i BankID-appen',
  },
  step3: {
    id: 'signup.bankid-session-status.steps.step-3',
    defaultMessage: '3. Rikta mobilkameran mot QR-koden nedan',
  },
  step4: {
    id: 'signup.bankid-session-status.steps.step-4',
    defaultMessage: '4. Legitimera dig i BankID-appen',
  },
})

export function getBankidSessionStatusMessage(status: BankIdOrderStatus, hintCode: BankIdOrderHintCode) {
  const defaultMessage =
    status === 'pending' ? messages.pending : status === 'complete' ? messages.complete : messages.unknown

  return messages[hintCode] || defaultMessage
}

interface IBankidSessionStatusProps {
  sessionId: string
  autoStartToken?: string | null
  bankIdQRImage?: string | null
  redirect?: string | null
  onComplete?: (data: DooerComGetBankIdSessionV2_bankIdSessionV2) => void
  onFailure?: (data?: DooerComGetBankIdSessionV2_bankIdSessionV2 | ApolloError) => void
  onBeforeNavigate?: () => void
}

const BankidSessionStatus = ({
  onComplete,
  onFailure,
  sessionId,
  autoStartToken,
  bankIdQRImage,
  redirect,
  onBeforeNavigate,
}: IBankidSessionStatusProps) => {
  const [pollInterval, setPollInterval] = React.useState<number>(1000)
  const [QRImage, setQRImage] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (bankIdQRImage) {
      qrImageFromQRInput(bankIdQRImage).then((qr) => setQRImage(qr))
    } else if (autoStartToken) {
      qrImageFromToken(autoStartToken).then((qr) => setQRImage(qr))
    }
  }, [autoStartToken, bankIdQRImage])

  return (
    <Query<DooerComGetBankIdSessionV2, DooerComGetBankIdSessionV2Variables>
      pollInterval={pollInterval}
      query={getBankidSession}
      variables={{ id: sessionId }}
    >
      {({ data, error, stopPolling }) => {
        if (data && data.bankIdSessionV2) {
          if (data.bankIdSessionV2.status !== 'pending') {
            stopPolling()
          }
          if (data.bankIdSessionV2.status === 'complete' && onComplete) {
            onComplete(data.bankIdSessionV2)
          }
          if (data.bankIdSessionV2.status === 'failed' && onFailure) {
            onFailure(data.bankIdSessionV2)
          }
        }
        if (error) {
          stopPolling()
          if (onFailure) {
            onFailure(error)
          }
        }

        const clientIsMobileOrTablet = isMobile().phone || isMobile().tablet
        if (data?.bankIdSessionV2?.bankIdQRImage) {
          qrImageFromQRInput(data?.bankIdSessionV2?.bankIdQRImage).then((qr) => setQRImage(qr))
        }

        return error ? (
          <ApolloErrorMessage error={error} />
        ) : data && data.bankIdSessionV2 && data.bankIdSessionV2.status && data.bankIdSessionV2.hintCode ? (
          <>
            {QRImage && !clientIsMobileOrTablet ? (
              <BankIdQrGuide />
            ) : !autoStartToken ? (
              <FormattedMessage
                {...getBankidSessionStatusMessage(data.bankIdSessionV2.status, data.bankIdSessionV2.hintCode)}
              />
            ) : null}

            {clientIsMobileOrTablet && (
              <div>
                <a
                  href={`https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${redirect || 'null'}`}
                  onClick={() => {
                    if (onBeforeNavigate) {
                      setPollInterval(0)
                      onBeforeNavigate()
                    }
                  }}
                >
                  <FormattedMessage {...messages.openOnDevice} />
                </a>
              </div>
            )}
            {QRImage && !clientIsMobileOrTablet && (
              <div style={{ marginTop: '2rem' }}>
                <img alt="qr" src={QRImage} />
              </div>
            )}
          </>
        ) : null
      }}
    </Query>
  )
}

const BankIdQrGuide = () => {
  const steps = [messages.step1, messages.step2, messages.step3, messages.step4]

  return (
    <div {...css({ textAlign: 'left', listStyleType: 'none', margin: '2rem 0' })}>
      <h4>
        <FormattedMessage {...messages.bankIdQrGuideHeading} />
      </h4>
      {steps.map((step: MessageDescriptor) => (
        <li key={step.id}>
          <FormattedMessage {...step} />
        </li>
      ))}
    </div>
  )
}

export default BankidSessionStatus
